import React from 'react';
import { Plus, Trash2, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TabularNumber } from '@/components/common/TabularNumber';
import { ArrivalItemInput } from '@/services/arrivalService';

interface ArrivalWeightsGridProps {
  items: ArrivalItemInput[];
  onChange: (items: ArrivalItemInput[]) => void;
  disabled?: boolean;
}

export const ArrivalWeightsGrid: React.FC<ArrivalWeightsGridProps> = ({
  items,
  onChange,
  disabled = false,
}) => {
  const handleAddRow = () => {
    const nextSeq = items.length > 0 ? Math.max(...items.map((i) => i.sequence_no)) + 1 : 1;
    onChange([
      ...items,
      {
        sequence_no: nextSeq,
        weight_kg: 0,
        tag_no: '',
        health_status: 'healthy',
        notes: '',
      },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    const updated = items.filter((_, idx) => idx !== index);
    onChange(updated);
  };

  const handleUpdateRow = (index: number, field: keyof ArrivalItemInput, value: any) => {
    const updated = [...items];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    onChange(updated);
  };

  const totalWeight = items.reduce((sum, item) => sum + (Number(item.weight_kg) || 0), 0);
  const avgWeight = items.length > 0 ? totalWeight / items.length : 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scale className="w-4 h-4 text-emerald-700" />
          <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            Individual Paper Weighing Sheet Weights ({items.length} logged)
          </span>
        </div>
        {!disabled && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleAddRow}
            className="h-7 text-xs px-2 text-emerald-800 border-emerald-300 hover:bg-emerald-50"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Pig Row
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="p-4 text-center border border-dashed border-slate-300 rounded-lg bg-slate-50/50">
          <p className="text-xs text-slate-500">
            No individual weights logged yet. You can log bulk total weight above or add individual pig weights from the paper sheet.
          </p>
          {!disabled && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleAddRow}
              className="mt-2 h-7 text-xs text-slate-700"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add First Pig Weight
            </Button>
          )}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="max-h-60 overflow-y-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-600 font-semibold border-b border-slate-200 sticky top-0">
                <tr>
                  <th className="py-2 px-3 w-12 text-center">#</th>
                  <th className="py-2 px-3 w-28 text-right">Weight (kg) *</th>
                  <th className="py-2 px-3 w-28">Ear Tag #</th>
                  <th className="py-2 px-3">Notes</th>
                  {!disabled && <th className="py-2 px-2 w-10 text-center"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {items.map((item, index) => (
                  <tr key={index} className="hover:bg-slate-50">
                    <td className="py-1.5 px-3 text-center font-mono text-slate-500 font-medium">
                      {item.sequence_no || index + 1}
                    </td>
                    <td className="py-1.5 px-2">
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        tabular
                        inputMode="decimal"
                        disabled={disabled}
                        value={item.weight_kg === 0 ? '' : item.weight_kg}
                        onChange={(e) =>
                          handleUpdateRow(index, 'weight_kg', parseFloat(e.target.value) || 0)
                        }
                        placeholder="0.0"
                        className="h-7 text-xs font-mono"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddRow();
                          }
                        }}
                      />
                    </td>
                    <td className="py-1.5 px-2">
                      <Input
                        type="text"
                        disabled={disabled}
                        value={item.tag_no || ''}
                        onChange={(e) => handleUpdateRow(index, 'tag_no', e.target.value)}
                        placeholder="Tag / Notch"
                        className="h-7 text-xs"
                      />
                    </td>
                    <td className="py-1.5 px-2">
                      <Input
                        type="text"
                        disabled={disabled}
                        value={item.notes || ''}
                        onChange={(e) => handleUpdateRow(index, 'notes', e.target.value)}
                        placeholder="Healthy / small runt / etc."
                        className="h-7 text-xs"
                      />
                    </td>
                    {!disabled && (
                      <td className="py-1.5 px-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveRow(index)}
                          className="text-slate-400 hover:text-red-600 p-1 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Table Summary Footer */}
          <div className="bg-slate-50 p-2 px-4 border-t border-slate-200 flex items-center justify-between text-xs font-mono font-bold text-slate-800">
            <span>Total Scale Weight: {totalWeight.toFixed(2)} kg</span>
            <span>Avg Weight: {avgWeight.toFixed(2)} kg/head</span>
          </div>
        </div>
      )}
    </div>
  );
};
